import React, {useEffect, useMemo, useRef, useState} from "react";
import {
    Badge,
    Box,
    Button,
    Flex,
    HStack,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Spinner,
    Text,
    Tooltip,
    useColorModeValue,
    VStack,
} from "@chakra-ui/react";
import {RepeatIcon, SearchIcon} from "@chakra-ui/icons";
import {FaBell, FaBullhorn, FaCheck, FaMusic, FaPlay, FaRegClock, FaStop} from "react-icons/fa";
import {FiBellOff} from "react-icons/fi";
import TimerSpinner from "./TimerSpinner";
import {typeBadgeMap} from "../../../../../utils/BadgeUtils";
import {BELL_INFO_STORAGE_KEY, BELL_INFO_UPDATED_EVENT, readBellInfo} from "../../../../../utils/BellStorageUtils";
import {getRemoteBellUrl} from "../../../../../utils/BellSoundCache";
import {playBellPreview} from "../../../../../utils/BellPreviewPlayer";
import {
    ANNOUNCEMENT_SCHEDULES_STORAGE_KEY,
    ANNOUNCEMENT_SCHEDULES_UPDATED_EVENT,
    readAnnouncementSchedules,
} from "../../../../../utils/AnnouncementStorageUtils";

// convert "hh:mm AM/PM" to minutes-since-midnight (for sorting)
const timeToMinutes = (time) => {
    if (!time) return Number.MAX_SAFE_INTEGER;
    const [clock, modifier] = time.trim().split(" ");
    let [hours, minutes] = (clock || "").split(":").map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return Number.MAX_SAFE_INTEGER;
    if (modifier?.toUpperCase() === "PM" && hours !== 12) hours += 12;
    if (modifier?.toUpperCase() === "AM" && hours === 12) hours = 0;
    return hours * 60 + minutes;
};

// compare "hh:mm AM/PM" with current time
const isTimePast = (bellTime) => {
    const minutes = timeToMinutes(bellTime);
    if (minutes === Number.MAX_SAFE_INTEGER) return false;
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes() > minutes;
};

// A scheduled announcement belongs on today's timeline when it is active and,
// if it carries a day-of-week filter, today is included.
const isScheduledToday = (announcement) => {
    if (!announcement || announcement.status !== "Y") return false;
    if (Array.isArray(announcement.days) && announcement.days.length > 0) {
        return announcement.days.includes(new Date().getDay());
    }
    return true;
};

// Shared column widths so the header row lines up with each queue row.
// The queue sits in the left grid column, whose width is NOT monotonic with the
// viewport: it is full-width at `md` (single column), narrows again at `lg`
// (two-column) and widens at `xl`. So the music *name* — and the wider music
// column — are only shown at `md` and `xl`+, and collapse to a compact
// play-button-only column at the cramped `lg` width and on mobile.
const MUSIC_COL_W = {base: "42px", md: "150px", lg: "42px", xl: "150px"};
const MUSIC_NAME_DISPLAY = {base: "none", md: "flex", lg: "none", xl: "flex"};
const STATUS_COL_W = {base: "88px", md: "120px"};

// Interactive bell-music cell: shows the assigned music name and a play/stop
// button so an operator can manually preview / ring the bell's sound on demand.
// Playback is remote-first (the backend R2 url) with a bundled-sound fallback,
// and only one preview is ever audible at a time (enforced by playBellPreview).
const BellMusicCell = ({bell}) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const previewRef = useRef(null);

    const source = getRemoteBellUrl(bell) || bell?.bell || bell?.fileUrl || bell?.url;
    const musicName = bell?.bellName || "Default bell";
    const canPlay = Boolean(source || bell?.bellName);

    const handlePlay = () => {
        previewRef.current = playBellPreview(source, {
            onStart: () => setIsPlaying(true),
            onEnded: () => {
                previewRef.current = null;
                setIsPlaying(false);
            },
            onError: () => {
                previewRef.current = null;
                setIsPlaying(false);
            },
            onStop: () => {
                previewRef.current = null;
                setIsPlaying(false);
            },
        });
    };

    const handleStop = () => {
        previewRef.current?.stop();
        previewRef.current = null;
        setIsPlaying(false);
    };

    // Stop playback if the row unmounts (e.g. on refresh / search filtering).
    useEffect(() => () => previewRef.current?.stop(), []);

    return (
        <Flex w={MUSIC_COL_W} flexShrink={0} align="center" gap={2} minW="0">
            <Tooltip
                label={isPlaying ? "Stop" : `Play "${musicName}"`}
                placement="top"
                hasArrow
            >
                <IconButton
                    aria-label={isPlaying ? "Stop music" : `Play ${musicName}`}
                    icon={<Icon as={isPlaying ? FaStop : FaPlay} boxSize={3}/>}
                    size="sm"
                    isRound
                    flexShrink={0}
                    colorScheme={isPlaying ? "red" : "teal"}
                    variant={isPlaying ? "solid" : "outline"}
                    isDisabled={!canPlay}
                    onClick={isPlaying ? handleStop : handlePlay}
                />
            </Tooltip>
            <HStack spacing={1} minW="0" flex="1" overflow="hidden" display={MUSIC_NAME_DISPLAY}>
                <Icon
                    as={FaMusic}
                    boxSize={3}
                    flexShrink={0}
                    color={isPlaying ? "teal.500" : "gray.400"}
                />
                <Text
                    fontSize="xs"
                    fontWeight="600"
                    noOfLines={1}
                    minW="0"
                    color={isPlaying ? "teal.600" : "gray.600"}
                    _dark={{color: isPlaying ? "teal.300" : "gray.300"}}
                >
                    {musicName}
                </Text>
            </HStack>
        </Flex>
    );
};

// Interactive "play time in seconds" chip. Shows the duration with a clock icon
// and a tooltip, and highlights on hover. Falls back to a dash when unknown.
const PlayTimeChip = ({seconds}) => {
    const value = Number(seconds);
    const hasValue = seconds !== null && seconds !== undefined && seconds !== "" && !Number.isNaN(value);

    if (!hasValue) {
        return (
            <Text fontSize="xs" color="gray.400" fontFamily="mono">
                —
            </Text>
        );
    }

    return (
        <Tooltip
            label={`Plays for ${value} second${value === 1 ? "" : "s"}`}
            placement="top"
            hasArrow
        >
            <HStack
                as="span"
                spacing={1}
                px={2}
                py={1}
                borderRadius="full"
                bg="gray.100"
                cursor="default"
                transition="all 0.15s ease"
                _hover={{bg: "teal.100", transform: "translateY(-1px)"}}
                _dark={{bg: "whiteAlpha.200", _hover: {bg: "teal.700"}}}
            >
                <Icon as={FaRegClock} boxSize={3} color="teal.500"/>
                <Text fontSize="xs" fontWeight="700" fontFamily="mono">
                    {value}s
                </Text>
            </HStack>
        </Tooltip>
    );
};

const BellTable = () => {
    const [bells, setBells] = useState([]);
    const [announcements, setAnnouncements] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [isPlaying, setIsPlaying] = useState(localStorage.getItem("isPlaying") === "true");
    const [currentBellId, setCurrentBellId] = useState(Number(localStorage.getItem("currentBellId")));

    // Header divider + interactive scrollbar colours (light/dark).
    const headerBorder = useColorModeValue("gray.100", "gray.700");
    const scrollThumb = useColorModeValue("rgba(0,0,0,0.16)", "rgba(255,255,255,0.22)");
    const scrollThumbHover = useColorModeValue("rgba(0,0,0,0.3)", "rgba(255,255,255,0.4)");

    useEffect(() => {
        fetchBellList();
        fetchAnnouncements();
    }, []);

    // Keep the virtual announcement schedules in sync (same-tab event + cross-tab storage).
    useEffect(() => {
        const onSchedulesUpdated = (e) =>
            setAnnouncements(Array.isArray(e.detail?.schedules) ? e.detail.schedules : readAnnouncementSchedules());
        const onStorage = (e) => {
            if (e.key === ANNOUNCEMENT_SCHEDULES_STORAGE_KEY) fetchAnnouncements();
        };

        window.addEventListener(ANNOUNCEMENT_SCHEDULES_UPDATED_EVENT, onSchedulesUpdated);
        window.addEventListener("storage", onStorage);
        return () => {
            window.removeEventListener(ANNOUNCEMENT_SCHEDULES_UPDATED_EVENT, onSchedulesUpdated);
            window.removeEventListener("storage", onStorage);
        };
    }, []);

    // Listen to BOTH: custom event (same-tab) + storage (other tabs)
    useEffect(() => {
        const onCustom = (e) => {
            if (e.detail?.key === "isPlaying") setIsPlaying(String(e.detail.value) === "true");
            if (e.detail?.key === "currentBellId") setCurrentBellId(Number(e.detail.value));
        };
        const onStorage = (e) => {
            if (e.key === "isPlaying") setIsPlaying(localStorage.getItem("isPlaying") === "true");
            if (e.key === "currentBellId") setCurrentBellId(Number(localStorage.getItem("currentBellId")));
            if (e.key === BELL_INFO_STORAGE_KEY) fetchBellList();
        };
        const onBellInfoUpdated = () => fetchBellList();

        window.addEventListener("bell:localstorage", onCustom);
        window.addEventListener(BELL_INFO_UPDATED_EVENT, onBellInfoUpdated);
        window.addEventListener("storage", onStorage);
        return () => {
            window.removeEventListener("bell:localstorage", onCustom);
            window.removeEventListener(BELL_INFO_UPDATED_EVENT, onBellInfoUpdated);
            window.removeEventListener("storage", onStorage);
        };
    }, []);

    const fetchBellList = async () => {
        try {
            setIsLoading(true);
            setBells(readBellInfo());
        } catch (e) {
            console.log("Error while fetching bells", e);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAnnouncements = () => {
        try {
            setAnnouncements(readAnnouncementSchedules());
        } catch (e) {
            console.log("Error while fetching announcements", e);
        }
    };

    // Merge bells and today's scheduled announcements into one timeline sorted by time.
    const queueItems = useMemo(() => {
        const bellItems = (bells || []).map((bell) => ({
            kind: "bell",
            id: `bell-${bell.id}`,
            time: bell.hourTime,
            name: bell.name,
            bell,
        }));
        const announcementItems = (announcements || [])
            .filter(isScheduledToday)
            .map((announcement) => ({
                kind: "announcement",
                id: `ann-${announcement.id}`,
                time: announcement.time,
                name: announcement.title || "Virtual announcement",
                announcement,
            }));

        return [...bellItems, ...announcementItems].sort(
            (a, b) => timeToMinutes(a.time) - timeToMinutes(b.time)
        );
    }, [bells, announcements]);

    const filteredItems = useMemo(
        () => queueItems.filter((item) => item.name?.toLowerCase().includes(searchTerm.toLowerCase())),
        [queueItems, searchTerm]
    );

    // first upcoming (not past) bell -> "Next"
    const nextBellId = useMemo(() => {
        const upcoming = (bells || []).filter((b) => !isTimePast(b.hourTime));
        return upcoming.length ? upcoming[0].id : null;
    }, [bells]);

    const getStatus = (bell) => {
        if (currentBellId === bell.id && isPlaying) {
            return {key: "playing", label: "Playing", color: "green", dot: "green.400"};
        }
        if (isTimePast(bell.hourTime)) {
            return {key: "past", label: "बज्यो", color: "gray", dot: "gray.300"};
        }
        return {key: "upcoming", label: "आउन लागेको", color: "blue", dot: "blue.400"};
    };

    const getAnnouncementStatus = (announcement) => {
        if (isTimePast(announcement.time)) {
            return {key: "past", label: "प्रसारित", color: "gray", dot: "gray.300"};
        }
        return {key: "upcoming", label: "आउन लागेको", color: "purple", dot: "purple.400"};
    };

    const renderBellRow = (bell) => {
        const status = getStatus(bell);
        const isNext = bell.id === nextBellId && status.key !== "playing";
        const typeMeta = typeBadgeMap[bell.type];

        return (
            <Flex
                align="center"
                gap={{base: 3, md: 4}}
                px={{base: 3, md: 4}}
                py={{base: 2, md: 2.5}}
                borderRadius="12px"
                borderWidth="1px"
                borderColor={
                    status.key === "playing"
                        ? "green.300"
                        : isNext
                            ? "blue.200"
                            : "gray.100"
                }
                bg={
                    status.key === "playing"
                        ? "green.50"
                        : isNext
                            ? "blue.50"
                            : "white"
                }
                opacity={status.key === "past" ? 0.7 : 1}
                transition="all 0.2s ease"
                _hover={{transform: "translateX(2px)", boxShadow: "sm"}}
                _dark={{bg: status.key === "playing" ? "green.900" : "gray.800", borderColor: "gray.700"}}
            >
                {/* Timeline dot */}
                <Flex
                    align="center"
                    justify="center"
                    w="32px"
                    h="32px"
                    borderRadius="full"
                    bg={status.dot}
                    color="white"
                    flexShrink={0}
                >
                    <Icon
                        as={
                            status.key === "playing"
                                ? FaBell
                                : status.key === "past"
                                    ? FaCheck
                                    : FaRegClock
                        }
                        boxSize={3.5}
                    />
                </Flex>

                {/* Bell time + name (with the play duration merged in) */}
                <Box flex="1" minW="0">
                    <HStack spacing={2} flexWrap="wrap">
                        <Text fontSize={{base: "md", md: "lg"}} fontWeight="700" fontFamily="mono">
                            {bell.hourTime}
                        </Text>
                        <PlayTimeChip seconds={bell.playTime}/>
                        {isNext && (
                            <Badge colorScheme="blue" borderRadius="full" px={2} textTransform="none">
                                Next
                            </Badge>
                        )}
                        {typeMeta && (
                            <Badge
                                colorScheme={typeMeta.colorScheme}
                                borderRadius="full"
                                px={2}
                                textTransform="none"
                                display={{base: "none", sm: "inline-flex"}}
                            >
                                {typeMeta.label}
                            </Badge>
                        )}
                    </HStack>
                    <Text fontSize="sm" color="gray.600" noOfLines={1} _dark={{color: "gray.300"}}>
                        {bell.name}
                    </Text>
                </Box>

                {/* Bell music + manual play */}
                <BellMusicCell bell={bell}/>

                {/* Status / timer */}
                <HStack w={STATUS_COL_W} spacing={3} flexShrink={0} justify="flex-end">
                    {status.key === "playing" && bell.playTime ? (
                        <TimerSpinner playDuration={bell.playTime}/>
                    ) : (
                        <Badge
                            colorScheme={status.color}
                            variant="subtle"
                            borderRadius="full"
                            px={3}
                            py={1.5}
                            textTransform="none"
                        >
                            {status.label}
                        </Badge>
                    )}
                </HStack>
            </Flex>
        );
    };

    const renderAnnouncementRow = (announcement) => {
        const status = getAnnouncementStatus(announcement);

        return (
            <Flex
                align="center"
                gap={{base: 3, md: 4}}
                px={{base: 3, md: 4}}
                py={{base: 2, md: 2.5}}
                borderRadius="12px"
                borderWidth="1px"
                borderStyle="dashed"
                borderColor={status.key === "past" ? "gray.200" : "purple.200"}
                bg={status.key === "past" ? "white" : "purple.50"}
                opacity={status.key === "past" ? 0.7 : 1}
                transition="all 0.2s ease"
                _hover={{transform: "translateX(2px)", boxShadow: "sm"}}
                _dark={{bg: status.key === "past" ? "gray.800" : "purple.900", borderColor: "gray.700"}}
            >
                {/* Timeline dot */}
                <Flex
                    align="center"
                    justify="center"
                    w="32px"
                    h="32px"
                    borderRadius="full"
                    bg={status.dot}
                    color="white"
                    flexShrink={0}
                >
                    <Icon as={status.key === "past" ? FaCheck : FaBullhorn} boxSize={3.5}/>
                </Flex>

                {/* Time + title (with the play duration merged in) */}
                <Box flex="1" minW="0">
                    <HStack spacing={2} flexWrap="wrap">
                        <Text fontSize={{base: "md", md: "lg"}} fontWeight="700" fontFamily="mono">
                            {announcement.time}
                        </Text>
                        <PlayTimeChip seconds={announcement.playTime ?? announcement.duration}/>
                        <Badge
                            colorScheme="purple"
                            borderRadius="full"
                            px={2}
                            textTransform="none"
                            display={{base: "none", sm: "inline-flex"}}
                        >
                            घोषणा
                        </Badge>
                    </HStack>
                    <Text fontSize="sm" color="gray.600" noOfLines={1} _dark={{color: "gray.300"}}>
                        {announcement.title || "Virtual announcement"}
                    </Text>
                </Box>

                {/* Music column — announcements carry a voice recording, not a
                    bell-music track, so keep the column aligned with a label. */}
                <Flex w={MUSIC_COL_W} flexShrink={0} align="center" gap={2} minW="0" color="gray.400" _dark={{color: "gray.500"}}>
                    <Flex align="center" justify="center" w="32px" flexShrink={0}>
                        <Icon as={FaBullhorn} boxSize={3.5}/>
                    </Flex>
                    <Text fontSize="xs" fontWeight="600" noOfLines={1} minW="0" flex="1" display={MUSIC_NAME_DISPLAY}>
                        घोषणा
                    </Text>
                </Flex>

                {/* Status */}
                <HStack w={STATUS_COL_W} spacing={3} flexShrink={0} justify="flex-end">
                    <Badge
                        colorScheme={status.color}
                        variant="subtle"
                        borderRadius="full"
                        px={3}
                        py={1.5}
                        textTransform="none"
                    >
                        {status.label}
                    </Badge>
                </HStack>
            </Flex>
        );
    };

    return (
        <Box maxW="100%" mx="auto" minW="0">
            {/* Controls */}
            <Flex mb={5} align={{base: "stretch", md: "center"}} direction={{base: "column", md: "row"}} gap={3}>
                <Box>
                    <Text fontSize="lg" fontWeight="700" lineHeight="1.2">Bell Queue</Text>
                    <Text fontSize="xs" color="gray.500">Today's bell timeline</Text>
                </Box>
                <InputGroup flex="1" minW="0" maxW={{md: "320px"}} ms={{md: "auto"}}>
                    <InputLeftElement pointerEvents="none">
                        <SearchIcon color="gray.300"/>
                    </InputLeftElement>
                    <Input
                        type="text"
                        placeholder="Search bells"
                        value={searchTerm}
                        borderRadius="full"
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </InputGroup>
                <Button colorScheme="teal" variant="ghost" onClick={fetchBellList} leftIcon={<RepeatIcon/>}>
                    Refresh
                </Button>
                <Button
                    colorScheme="red"
                    variant="outline"
                    leftIcon={<FiBellOff/>}
                    onClick={() => setBells((prev) => prev.map((b) => ({...b, status: "Inactive", queue: 0})))}
                    whiteSpace="normal"
                    h={{base: "auto", md: "40px"}}
                    py={{base: 2, md: 0}}
                >
                    Stop for Today
                </Button>
            </Flex>

            {isLoading ? (
                <Flex justifyContent="center" alignItems="center" minH="200px">
                    <Spinner size="xl" color="teal.500"/>
                </Flex>
            ) : filteredItems.length === 0 ? (
                <Flex direction="column" align="center" justify="center" py={12} color="gray.400">
                    <Icon as={FaBell} boxSize={8} mb={3}/>
                    <Text>No bells in the queue.</Text>
                </Flex>
            ) : (
                <>
                    {/* Scrollable timeline. The header sticks to the top of the
                        scroll area, and the styled scrollbar keeps a long queue
                        contained without pushing the rest of the page down. */}
                    {/* Fixed column header — kept OUTSIDE the scroll area so only
                        the rows scroll. It reserves the same scrollbar gutter as
                        the list (scrollbar-gutter: stable) and matches each row's
                        box model (1px border + padding) so every column lines up. */}
                    <Box
                        display={{base: "none", sm: "block"}}
                        overflowY="auto"
                        sx={{
                            scrollbarGutter: "stable",
                            scrollbarWidth: "thin",
                            scrollbarColor: "transparent transparent",
                            "&::-webkit-scrollbar": {width: "8px"},
                            "&::-webkit-scrollbar-track": {background: "transparent"},
                            "&::-webkit-scrollbar-thumb": {background: "transparent"},
                        }}
                    >
                        <Flex
                            align="center"
                            gap={{base: 3, md: 4}}
                            px={{base: 3, md: 4}}
                            py={2}
                            borderWidth="1px"
                            borderColor="transparent"
                            borderBottomColor={headerBorder}
                            color="gray.500"
                            fontSize="xs"
                            fontWeight="700"
                            textTransform="uppercase"
                            letterSpacing="wider"
                            _dark={{color: "gray.400"}}
                        >
                            <Box w="32px" flexShrink={0}/>
                            <Text flex="1" minW="0">Bell &amp; Time</Text>
                            <Text w={MUSIC_COL_W} flexShrink={0} textAlign="left">
                                Music
                            </Text>
                            <Text w={STATUS_COL_W} flexShrink={0} textAlign="right">
                                Status
                            </Text>
                        </Flex>
                    </Box>

                    {/* Scrollable rows only — same stable gutter keeps the columns
                        aligned with the fixed header above. */}
                    <Box
                        mt={2}
                        maxH={{base: "56vh", md: "440px", xl: "520px"}}
                        overflowY="auto"
                        overflowX="hidden"
                        sx={{
                            scrollbarGutter: "stable",
                            scrollBehavior: "smooth",
                            scrollbarWidth: "thin",
                            scrollbarColor: `${scrollThumb} transparent`,
                            "&::-webkit-scrollbar": {width: "8px"},
                            "&::-webkit-scrollbar-track": {background: "transparent"},
                            "&::-webkit-scrollbar-thumb": {
                                background: scrollThumb,
                                borderRadius: "8px",
                            },
                            "&::-webkit-scrollbar-thumb:hover": {background: scrollThumbHover},
                        }}
                    >
                        <VStack align="stretch" spacing={2} position="relative">
                            {filteredItems.map((item) => (
                                <React.Fragment key={item.id}>
                                    {item.kind === "announcement"
                                        ? renderAnnouncementRow(item.announcement)
                                        : renderBellRow(item.bell)}
                                </React.Fragment>
                            ))}
                        </VStack>
                    </Box>

                    {/* Count + scroll hint. */}
                    <Flex justify="space-between" align="center" mt={3} px={1} color="gray.500">
                        <Text fontSize="xs">
                            {filteredItems.length} {filteredItems.length === 1 ? "item" : "items"}
                            {searchTerm ? ` for “${searchTerm}”` : ""}
                        </Text>
                        {filteredItems.length > 6 && (
                            <HStack spacing={1} fontSize="xs" color="gray.400">
                                <Icon as={FaRegClock} boxSize={2.5}/>
                                <Text>Scroll for more</Text>
                            </HStack>
                        )}
                    </Flex>
                </>
            )}
        </Box>
    );
};

export default BellTable;
